import React from 'react';
import { AudioButton } from './AudioButton';
import { MicrophoneButton } from './MicrophoneButton';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { RotateCcw, Check, X, Loader2, BookOpen } from 'lucide-react';

const CAT_LABELS = {
  greeting: 'Begrüßung', phrase: 'Sätze', work: 'Beruf', adjective: 'Adjektive',
  noun: 'Nomen', verb: 'Verben', family: 'Familie', home: 'Wohnen',
  technology: 'Technik', hobby: 'Hobby', time: 'Zeit', day: 'Tage',
  food: 'Essen', transport: 'Verkehr', direction: 'Richtung', place: 'Orte',
  daily_routine: 'Tagesablauf', celebration: 'Feste', adverb: 'Adverbien',
  clothing: 'Kleidung', color: 'Farben', shopping: 'Einkaufen', education: 'Bildung',
  body: 'Körper', health: 'Gesundheit', person: 'Person', appearance: 'Aussehen',
  travel: 'Reisen', media: 'Medien', communication: 'Kommunikation',
  hospital: 'Krankenhaus', body_medical: 'Körper (med.)', symptoms: 'Symptome',
  vital_signs: 'Vitalzeichen', medication: 'Medikamente', verb_medical: 'Pflege-Verben',
  nutrition: 'Ernährung', shifts: 'Schichten', emergency: 'Notfall',
  documentation: 'Dokumentation', equipment: 'Geräte', phrase_medical: 'Pflege-Sätze',
};

export { CAT_LABELS };

export function SRSReviewPanel({
  cards, currentIndex, currentCard, isFlipped, loading,
  sessionStats, pronunciationScore, reviewing,
  onFlip, onReview, onFetchDue, onSwitchToDictionary, onNavigateDashboard,
  getCardText, setPronunciationScore
}) {
  const isSessionComplete = currentIndex >= cards.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 py-4">
        <Progress value={cards.length > 0 ? ((currentIndex) / cards.length) * 100 : 0} className="h-2 bg-slate-700" />
        <p className="text-white/60 text-sm mt-2 text-center">{Math.min(currentIndex + 1, cards.length)} / {cards.length} cards</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {isSessionComplete || cards.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700 text-white text-center py-12">
            <CardContent>
              {cards.length === 0 ? (
                <>
                  <div className="text-5xl mb-4">&#10024;</div>
                  <h2 className="font-heading text-2xl font-bold mb-2">All caught up!</h2>
                  <p className="text-white/60 mb-6">No cards due for review. Try the dictionary!</p>
                  <Button onClick={onSwitchToDictionary} className="bg-teal-500 hover:bg-teal-600">
                    <BookOpen className="h-4 w-4 mr-2" /> Open Dictionary
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-5xl mb-4">&#127881;</div>
                  <h2 className="font-heading text-2xl font-bold mb-4">Session Complete!</h2>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-500/20 rounded-xl p-4"><p className="text-3xl font-bold text-green-400">{sessionStats.correct}</p><p className="text-green-400/70">Correct</p></div>
                    <div className="bg-red-500/20 rounded-xl p-4"><p className="text-3xl font-bold text-red-400">{sessionStats.incorrect}</p><p className="text-red-400/70">To Review</p></div>
                  </div>
                  <div className="flex gap-4 justify-center">
                    <Button onClick={onFetchDue} variant="outline" className="border-white/30 text-white"><RotateCcw className="h-4 w-4 mr-2" /> Again</Button>
                    <Button onClick={onNavigateDashboard} className="bg-teal-500 hover:bg-teal-600">Dashboard</Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="perspective-1000 cursor-pointer mb-8" onClick={onFlip}>
              <div className={`relative w-full h-80 transition-transform duration-500 transform-style-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
                <Card className={`absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700 backface-hidden ${isFlipped ? 'invisible' : ''}`}>
                  <CardContent className="h-full flex flex-col items-center justify-center p-8">
                    <Badge className="mb-4 bg-teal-500">{CAT_LABELS[currentCard?.category] || currentCard?.category}</Badge>
                    <h2 className="font-heading text-3xl font-bold text-white text-center mb-4">{currentCard?.front_de}</h2>
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <AudioButton text={currentCard?.audio_text || currentCard?.front_de} voice="nova" />
                      <MicrophoneButton expectedText={currentCard?.front_de} onPronunciationScore={(score) => setPronunciationScore(score)} size="default" className="text-teal-400" />
                    </div>
                    {pronunciationScore && (
                      <div className="mt-3 text-center">
                        <span className={`text-2xl font-bold ${pronunciationScore.overall_score >= 80 ? 'text-green-400' : pronunciationScore.overall_score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>{pronunciationScore.overall_score}%</span>
                        <p className="text-white/50 text-xs">Pronunciation</p>
                      </div>
                    )}
                    <p className="text-white/50 text-sm mt-4">Tap to flip</p>
                  </CardContent>
                </Card>

                <Card className={`absolute inset-0 bg-gradient-to-br from-teal-900 to-slate-900 border-teal-700 backface-hidden rotate-y-180 ${!isFlipped ? 'invisible' : ''}`}>
                  <CardContent className="h-full flex flex-col items-center justify-center p-8">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="font-heading text-2xl font-bold text-white text-center">{getCardText(currentCard, 'back')}</h2>
                      <span onClick={(e) => e.stopPropagation()}><AudioButton text={currentCard?.front_de} voice="nova" /></span>
                    </div>
                    <p className="text-white/70 text-center mb-4 italic">&ldquo;{currentCard?.back_example}&rdquo;</p>
                    {currentCard?.back_example && <span onClick={(e) => e.stopPropagation()}><AudioButton text={currentCard?.back_example} voice="nova" size="sm" className="text-teal-300" /></span>}
                    <p className="text-white/50 text-sm mt-2">Tap to flip back</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {isFlipped ? (
              <div className="grid grid-cols-4 gap-3 animate-fade-in">
                <Button onClick={() => onReview(1)} disabled={reviewing} className="bg-red-500 hover:bg-red-600 text-white py-6"><X className="h-5 w-5 mr-1" /> Again</Button>
                <Button onClick={() => onReview(2)} disabled={reviewing} className="bg-orange-500 hover:bg-orange-600 text-white py-6">Hard</Button>
                <Button onClick={() => onReview(4)} disabled={reviewing} className="bg-teal-500 hover:bg-teal-600 text-white py-6">Good</Button>
                <Button onClick={() => onReview(5)} disabled={reviewing} className="bg-green-500 hover:bg-green-600 text-white py-6"><Check className="h-5 w-5 mr-1" /> Easy</Button>
              </div>
            ) : (
              <div className="text-center">
                <Button onClick={onFlip} size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-12">Show Answer</Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
